/**
 * @fileoverview Grid layout system for dashboard widgets
 *
 * Provides a configuration-based grid system that converts column/row spans
 * into calculated CSS positions for smooth animations with absolute positioning.
 */

import { createContext, useContext, useState, useCallback, useMemo, useRef } from 'react';

// Context for grid configuration
const GridContext = createContext({
  columns: 12,
  rows: 8,
  gap: 24,
  isEditing: false,
  layout: {},
  getWidgetPosition: () => ({}),
  onWidgetMove: () => {},
  onWidgetResize: () => {},
  gridContainerRef: null // Reference to grid container for expanded widget portal
});

export const useGridContext = () => useContext(GridContext);

/**
 * Default grid layout configuration
 * Each widget has: colStart (1-indexed), colSpan, rowStart (1-indexed), rowSpan
 */
export const DEFAULT_LAYOUT = {
  timeWidget: { colStart: 1, colSpan: 4, rowStart: 1, rowSpan: 2 },
  statsGrid: { colStart: 5, colSpan: 8, rowStart: 1, rowSpan: 2 },
  mainCanvas: { colStart: 1, colSpan: 8, rowStart: 3, rowSpan: 6 },
  sidePanel: { colStart: 9, colSpan: 4, rowStart: 3, rowSpan: 6 }
};

/**
 * Convert grid position to CSS position values
 *
 * @param {Object} widgetLayout - Widget's grid position { colStart, colSpan, rowStart, rowSpan }
 * @param {number} columns - Total grid columns
 * @param {number} rows - Total grid rows
 * @param {number} gap - Gap between cells in pixels
 * @returns {Object} CSS position values { top, left, width, height }
 */
export function gridToPosition(widgetLayout, columns, rows, gap) {
  const { colStart, colSpan, rowStart, rowSpan } = widgetLayout;

  // Calculate cell size as percentage
  const cellWidth = 100 / columns;
  const cellHeight = 100 / rows;

  // Calculate gap offset - we need to account for gaps between cells
  // Gap is in pixels, so we'll use calc() to combine percentages with pixels
  const gapsBeforeCol = colStart - 1;
  const gapsBeforeRow = rowStart - 1;
  const gapsInWidth = colSpan - 1;
  const gapsInHeight = rowSpan - 1;

  // Position calculations
  const leftPercent = (colStart - 1) * cellWidth;
  const topPercent = (rowStart - 1) * cellHeight;
  const widthPercent = colSpan * cellWidth;
  const heightPercent = rowSpan * cellHeight;

  return {
    left: `calc(${leftPercent}% + ${gapsBeforeCol * gap}px)`,
    top: `calc(${topPercent}% + ${gapsBeforeRow * gap}px)`,
    width: `calc(${widthPercent}% - ${(gapsBeforeCol + 1) * gap - gapsInWidth * gap}px)`,
    height: `calc(${heightPercent}% - ${(gapsBeforeRow + 1) * gap - gapsInHeight * gap}px)`
  };
}

/**
 * Simplified position calculation that accounts for gaps properly
 *
 * The grid has (columns - 1) gaps horizontally and (rows - 1) gaps vertically.
 * Total width = 100% = (columns * cellWidth) + ((columns - 1) * gap)
 * So: cellWidth = (100% - (columns - 1) * gap) / columns
 */
export function gridToPositionSimple(widgetLayout, columns, rows, gap) {
  const { colStart, colSpan, rowStart, rowSpan } = widgetLayout;

  // Total gaps in the grid
  const totalHGaps = columns - 1;
  const totalVGaps = rows - 1;

  // Use calc() to subtract total gap space from 100%, then divide
  // Each cell = (100% - totalGaps * gap) / columns
  // Position left = (colStart - 1) * cellWidth + (colStart - 1) * gap
  // Width = colSpan * cellWidth + (colSpan - 1) * gap

  const gapsBeforeLeft = colStart - 1;
  const gapsBeforeTop = rowStart - 1;
  const gapsInWidth = colSpan - 1;
  const gapsInHeight = rowSpan - 1;

  return {
    left: `calc((100% - ${totalHGaps * gap}px) * ${(colStart - 1) / columns} + ${gapsBeforeLeft * gap}px)`,
    top: `calc((100% - ${totalVGaps * gap}px) * ${(rowStart - 1) / rows} + ${gapsBeforeTop * gap}px)`,
    width: `calc((100% - ${totalHGaps * gap}px) * ${colSpan / columns} + ${gapsInWidth * gap}px)`,
    height: `calc((100% - ${totalVGaps * gap}px) * ${rowSpan / rows} + ${gapsInHeight * gap}px)`
  };
}

/**
 * DashboardGrid - Grid configuration provider component
 *
 * @param {Object} props
 * @param {React.ReactNode} props.children - Grid children (DashboardWidget components)
 * @param {number} props.columns - Number of grid columns (default: 12)
 * @param {number} props.rows - Number of grid rows (default: 8)
 * @param {number} props.gap - Gap between grid items in pixels (default: 24)
 * @param {boolean} props.isEditing - Whether grid is in edit mode (default: false)
 * @param {Object} props.layout - Current layout configuration
 * @param {Function} props.onLayoutChange - Callback when layout changes
 * @param {string} props.className - Additional CSS classes
 */
function DashboardGrid({
  children,
  columns = 12,
  rows = 8,
  gap = 24,
  isEditing = false,
  layout = DEFAULT_LAYOUT,
  onLayoutChange,
  className = ''
}) {
  // Ref for the grid container (used for expanded widget portal)
  const gridContainerRef = useRef(null);

  // Calculate position for a widget
  const getWidgetPosition = useCallback((widgetId) => {
    const widgetLayout = layout[widgetId];
    if (!widgetLayout) {
      return { left: '0', top: '0', width: '100%', height: '100%' };
    }
    return gridToPositionSimple(widgetLayout, columns, rows, gap);
  }, [layout, columns, rows, gap]);

  // Handle widget movement (changes colStart/rowStart)
  const handleWidgetMove = useCallback((widgetId, deltaCol, deltaRow) => {
    if (!onLayoutChange) return;

    const widget = layout[widgetId];
    if (!widget) return;

    const newColStart = Math.max(1, Math.min(widget.colStart + deltaCol, columns - widget.colSpan + 1));
    const newRowStart = Math.max(1, Math.min(widget.rowStart + deltaRow, rows - widget.rowSpan + 1));

    const newLayout = {
      ...layout,
      [widgetId]: {
        ...widget,
        colStart: newColStart,
        rowStart: newRowStart
      }
    };
    onLayoutChange(newLayout);
  }, [layout, onLayoutChange, columns, rows]);

  // Handle widget resize (changes colSpan/rowSpan)
  const handleWidgetResize = useCallback((widgetId, deltaColSpan, deltaRowSpan) => {
    if (!onLayoutChange) return;

    const widget = layout[widgetId];
    if (!widget) return;

    const newColSpan = Math.max(1, Math.min(widget.colSpan + deltaColSpan, columns - widget.colStart + 1));
    const newRowSpan = Math.max(1, Math.min(widget.rowSpan + deltaRowSpan, rows - widget.rowStart + 1));

    const newLayout = {
      ...layout,
      [widgetId]: {
        ...widget,
        colSpan: newColSpan,
        rowSpan: newRowSpan
      }
    };
    onLayoutChange(newLayout);
  }, [layout, onLayoutChange, columns, rows]);

  // Generate grid overlay cells for edit mode
  const gridOverlayCells = useMemo(() => {
    if (!isEditing) return null;

    const cells = [];
    for (let row = 1; row <= rows; row++) {
      for (let col = 1; col <= columns; col++) {
        const position = gridToPositionSimple(
          { colStart: col, colSpan: 1, rowStart: row, rowSpan: 1 },
          columns,
          rows,
          gap
        );
        cells.push(
          <div
            key={`${row}-${col}`}
            className="absolute border border-dashed border-white/10 rounded pointer-events-none"
            style={{
              left: position.left,
              top: position.top,
              width: position.width,
              height: position.height
            }}
          />
        );
      }
    }
    return cells;
  }, [isEditing, columns, rows, gap]);

  const contextValue = useMemo(() => ({
    columns,
    rows,
    gap,
    isEditing,
    layout,
    getWidgetPosition,
    onWidgetMove: handleWidgetMove,
    onWidgetResize: handleWidgetResize,
    gridContainerRef
  }), [columns, rows, gap, isEditing, layout, getWidgetPosition, handleWidgetMove, handleWidgetResize]);

  return (
    <GridContext.Provider value={contextValue}>
      <div
        ref={gridContainerRef}
        className={`dashboard-grid relative w-full h-full ${className}`}
      >
        {/* Grid overlay for edit mode */}
        {gridOverlayCells}

        {children}
      </div>
    </GridContext.Provider>
  );
}

export default DashboardGrid;
